/**
 * Marketing copy and URLs — single source for HomePage sections.
 * Replace TODO URLs when ops publishes canonical environment links (see docs/4).
 *
 * Copy guardrail: Truss is a **security information exchange** (intel + data delivery),
 * not a financial or trading venue — avoid buy/sell/market/portfolio language.
 */

/** Matches package.json `homepage` — used for absolute OG/Twitter image URLs. */
export const siteOrigin = "https://truss-security.com";

/** Build-time public URLs (CRA: set `REACT_APP_*` in `.env` for deploy). */
export const publicUrls = {
	/** Public status page — leave unset until published; UI falls back gracefully. */
	status: process.env.REACT_APP_STATUS_PAGE_URL || "",
	/** Optional JSON health endpoint for a live “operational” chip (same-origin or CORS-enabled). */
	health: process.env.REACT_APP_PUBLIC_HEALTH_URL || "",
};

/**
 * Brand files live in `public/` — copied from `truss-dashboard/public` (Truss_Logo.png, Truss_favicon.ico, Truss_92x92.png).
 * Re-copy when dashboard branding changes.
 */
export const brandAssets = {
	logo: "/Truss_Logo.png",
	favicon: "/Truss_favicon.ico",
	icon92: "/Truss_92x92.png",
};

/**
 * Header mark — same as truss-dashboard `TopNavBar` (favicon + wordmark beside dashboard content).
 */
export const headerBrand = {
	iconSrc: brandAssets.favicon,
	wordmark: "Truss",
	/** Tailwind `w-10` / `h-8` */
	iconWidthPx: 40,
	iconHeightPx: 32,
};

export const urls = {
	/** Truss public dashboard */
	dashboard: "https://dashboard.truss-security.com",
	/** Published docs (Docusaurus source: truss-docs) */
	docsApiReadme: "https://docs.truss-security.com",
	/** GitHub Repositories */
	githubOrg: "https://github.com/truss-security", 
	sdkReadme: "https://github.com/truss-security/truss-sdk",
	trussAgent: "https://github.com/truss-security/truss-agent",
	trussAgentMcp: "https://github.com/truss-security/truss-agent-mcp",
	/** Hosted MCP connect URL (OAuth clients) — not a marketing page */
	mcpEndpoint: "https://api.truss-security.com/mcp",
	/** Public MCP docs / overview */
	mcpMarketing: "https://docs.truss-security.com/data/mcp",
	/** Official MCP registry name */
	mcpRegistry: "com.truss-security/truss-mcp",
	/** Smithery listing (homepage backlink required for verification) */
	mcpSmithery: "https://smithery.ai/servers/truss-security/truss-mcp",
	/** Cursor Directory plugin listing */
	mcpCursorMarketplace: "https://cursor.directory/plugins/truss-mcp",
	/** NPM package repository */
	npmPackage: "https://www.npmjs.com/package/@truss-security/truss-sdk",
	/** Public Truss Discord invite */
	discord: "https://discord.com/invite/zerVhHtfxJ",
	/** Placeholder API origin for code samples only — confirm for public docs */
	apiExampleBase: "https://api.truss-security.com",
};

export const sectionIds = {
	main: "main-content",
	hero: "hero",
	trust: "trust",
	proofLoop: "proof-loop",
	intent: "intent",
	platform: "platform",
	/** Alias to developers — MCP lives inside that section */
	mcp: "developers",
	developers: "developers",
	integrations: "integrations",
	community: "community",
	proof: "proof",
};

export const navItems = [
	{ label: "Product", to: `/#${sectionIds.trust}` },
	{ label: "Developers", to: `/#${sectionIds.developers}` },
	{ label: "Integrations", to: `/#${sectionIds.integrations}` },
	{ label: "Community", to: `/#${sectionIds.community}` },
	{ label: "Pricing", to: "/pricing" },
];

export const hero = {
	title: "Customized threat intelligence, tailored and configurable to your needs",
	subhead:
		"Pre-validated, immediately actionable products, delivered in near real time via API, STIX, and MCP, formatted to fit how you work.",
	ctas: [
		{ label: "Open dashboard", href: urls.dashboard, variant: "primary" },
		{ label: "API & docs", href: urls.docsApiReadme, variant: "secondary" },
		{ label: "MCP overview", href: urls.mcpMarketing, variant: "secondary" },
	],
};

/** Post-hero differentiators — Customizable, Speed, Validated */
export const trustBand = {
	title: "What Truss does best",
	lead:
		"Built for lean SOC and security engineering teams who need intel in the tools they already run — high quality, pre-validated, and fast.",
	points: [
		{
			title: "Customizable",
			body: "Tailor and configure coverage and delivery to your needs. NOT a generic feed dump.",
		},
		{
			title: "Speed",
			body: "Near-real-time access through API, STIX, and MCP.",
		},
		{
			title: "Validated",
			body: "Pre-validated and pre-formatted so the product is immediately actionable.",
		},
	],
};

/** Source → validate → search → deliver proof strip */
export const proofLoop = {
	eyebrow: "The Truss advantage",
	title: "From publication to your stack",
	lead:
		"Rigorous intelligence, delivered at operational speeds. Pre-parsed, validated, and formatted for your security tools the moment it shows up in the Truss dashboard.",
	leadDetail: {
		before:
			"Purpose-built architecture and a direct exchange model aggregate premium intelligence from validated sources. ",
		bold: "Without the overhead cost other vendors pass through to their clients",
		after:
			". Faster delivery, greater usability, and a price structure catered to Truss's unique business model.",
	},
	steps: [
		{
			key: "quality",
			label: "Quality",
			accent: "Rigor before release",
			body: "Validated and formatted before release—signal, not noise to triage.",
			icon: "quality",
		},
		{
			key: "speed",
			label: "Timely",
			accent: "Operational velocity",
			body: "Ready for your stack as soon as intelligence is available—not after an editorial queue.",
			icon: "speed",
		},
		{
			key: "value",
			label: "Value",
			accent: "The honest price",
			body: "Premium fidelity without the legacy markup.",
			icon: "value",
			featured: true,
		},
		{
			key: "deliver",
			label: "Deliver",
			accent: "Into your workflow",
			body: "Dashboard, TQL, or MCP → REST, STIX, and Agent into your stack.",
			icon: "deliver",
		},
	],
};

export const intentRamps = [
	{
		id: "explore",
		title: "Explore",
		description: "Search IOCs, maps, and timelines — save filters your team can share.",
		href: urls.dashboard,
		linkLabel: "Open dashboard",
	},
	{
		id: "integrate",
		title: "Integrate",
		description: "Pull validated intel into your stack with REST, STIX, and the TypeScript SDK.",
		href: urls.docsApiReadme,
		linkLabel: "API quickstart",
	},
	{
		id: "operate",
		title: "Operate",
		description: "Automate delivery of IOCs and reports to chat, SIEM, EDR, and more.",
		href: urls.trussAgent,
		linkLabel: "Agent install",
	},
	{
		id: "connect",
		title: "Connect",
		description: "Ask questions, share workflows, and learn with builders and analysts.",
		href: urls.discord,
		linkLabel: "Join Discord",
	},
];

/** Platform strip: Intelligence → Dashboard → API → Agent → destinations (docs 2–4) */
export const platformStrip = {
	title: "How Truss fits together",
	lead: "One exchange: discover intel, search it, deliver it, and automate into your stack.",
	layers: [
		{
			key: "intelligence",
			label: "Intelligence",
			blurb: "Discover — aggregated public and vendor security data in one place.",
			artTitle: "Illustration: intelligence layer with strategy, narrative, and knowledge",
		},
		{
			key: "dashboard",
			label: "Dashboard",
			blurb: "Search — explore and query from a single analyst workspace.",
			artTitle: "Illustration: dashboard with charts, maps, and filter panels",
		},
		{
			key: "api",
			label: "API",
			blurb: "Deliver — REST and STIX via the Truss SDK into your tools.",
			artTitle: "Illustration: API and developer tools, product search and SDK",
		},
		{
			key: "agent",
			label: "Agent",
			blurb: "Automate — schedule IOC, metadata, or executive report pushes.",
			artTitle: "Illustration: agent pulling from the API and formatting deliveries",
		},
		{
			key: "destinations",
			label: "Destinations",
			blurb: "Operate — land intel in chat, SIEM, SOAR, EDR, and more.",
			artTitle: "Illustration: intel fanning out to chat, SIEM, and EDR",
		},
	],
};

export const developerSurface = {
	title: "Ask, then deliver",
	lead:
		"Ask in Cursor or Claude → get matching products and IOCs → pull STIX into the tools you already run. Or use the TypeScript SDK when you need typed filters and STIX-ready outputs.",
	defaultModeId: "mcp",
	modes: [
		{
			id: "mcp",
			label: "MCP",
			chromeFallback: "truss-mcp",
			snippetLabel: "Connect config",
			snippet: `{
  "mcpServers": {
    "truss-mcp": {
      "url": "${urls.mcpEndpoint}"
    }
  }
}`,
			endpointLabel: "Connect URL",
			endpoint: urls.mcpEndpoint,
			toolsLabel: "Hosted tools",
			tools: ["lookup_ioc", "search_threats", "get_product", "get_product_stix", "search_stix"],
			eligibilityNote:
				"Hosted MCP uses OAuth consent. Deployment+ plans can connect; Community and Access accounts cannot consent.",
			primaryCta: { label: "MCP overview", href: urls.mcpMarketing },
			secondaryCta: { label: "GitHub: truss-agent-mcp", href: urls.trussAgentMcp },
			links: [
				{ label: "Truss MCP on Smithery", href: urls.mcpSmithery },
				{ label: "Truss MCP on Cursor Directory", href: urls.mcpCursorMarketplace },
			],
			defaultPreviewId: "cursor",
			previews: [
				{
					id: "cursor",
					label: "Cursor",
					chrome: "Cursor · Agent · truss-mcp",
					lines: [
						{
							role: "user",
							label: "You",
							text: "Any recent ransomware activity targeting healthcare in North America?",
						},
						{
							role: "tool",
							label: "Tool",
							text: "search_threats",
							detail: "category=Ransomware · region=North America · days=7",
						},
						{
							role: "assistant",
							label: "Cursor",
							text: "Truss returned 3 matching products. Top hit: Healthcare ransomware campaign notes (id: prod_8f2a…) with related IOCs available via get_product_stix.",
						},
					],
				},
				{
					id: "claude",
					label: "Claude",
					chrome: "Claude · truss-mcp",
					lines: [
						{
							role: "user",
							label: "You",
							text: "Look up this domain indicator and summarize what Truss knows.",
						},
						{
							role: "tool",
							label: "Tool",
							text: "lookup_ioc",
							detail: "type=domain · value=example-indicator[.]com",
						},
						{
							role: "assistant",
							label: "Claude",
							text: "Found in 2 Truss products. Primary association: phishing kit delivery notes. I can pull STIX with get_product_stix if you want structured observables.",
						},
					],
				},
				{
					id: "terminal",
					label: "Terminal",
					chrome: "truss-mcp search",
					lines: [
						{
							role: "system",
							label: "shell",
							text: "$ truss-mcp search",
						},
						{
							role: "user",
							label: "You",
							text: "Search last 7 days for malware tagged finance",
						},
						{
							role: "tool",
							label: "MCP",
							text: "→ search_threats",
							detail: "days=7 · limit=5",
						},
						{
							role: "system",
							label: "Results",
							text: "id              title                         category\nprod_1a2b…      Finance malware briefing      Malware\nprod_3c4d…      Banking trojan notes          Malware",
						},
						{
							role: "assistant",
							label: "Truss",
							text: "Two products matched. Offer: refine filter, export STIX, or draft hunting queries?",
						},
					],
				},
			],
		},
		{
			id: "sdk",
			label: "SDK",
			chrome: "TypeScript · @truss-security/truss-sdk",
			snippetLabel: "TypeScript (illustrative)",
			snippet: `import { TrussClient, filter } from "@truss-security/truss-sdk";

const truss = new TrussClient({
  baseUrl: process.env.TRUSS_API_BASE ?? "${urls.apiExampleBase}",
  apiKey: process.env.TRUSS_API_KEY,
});

const results = await truss.search.products({
  filter: filter.and(
    filter.eq("category", "Malware"),
    filter.anyOf("region", ["North America", "Europe"])
  ),
  days: 7,
  limit: 25,
});`,
			primaryCta: { label: "Install SDK", href: urls.npmPackage },
			secondaryCta: { label: "API & docs", href: urls.docsApiReadme },
			links: [
				{ label: "Truss SDK repository", href: urls.sdkReadme },
			],
		},
	],
};

export const integrationsRow = {
	title: "Integrations built for action",
	lead:
		"Use Truss intelligence where your teams already operate, from out-of-the-box delivery to API-enabled destinations.",
	groups: [
		{
			key: "ootb",
			title: "Out-of-the-box with truss-agent",
			description:
				"Start immediately with destination supported webhooks powered by truss-agent configuration.",
			destinations: [
				{ key: "discord", label: "Discord" },
				{ key: "slack", label: "Slack" },
				{ key: "teams", label: "Microsoft Teams" },
			],
		},
		{
			key: "apiEnabled",
			title: "API-enabled destinations",
			description:
				"Truss provides the intelligence and API surface to connect to your security tools. Using the Truss API, you can supply destination-specific connectivity for these tool categories.",
			destinations: [
				{ key: "siem", label: "SIEM" },
				{ key: "edr", label: "EDR" },
				{ key: "ngfw", label: "NGFW" },
				{ key: "ctiPlatforms", label: "CTI Platforms" },
				{ key: "emailSecOps", label: "Email Security" },
				{ key: "cloudSecPlatform", label: "Cloud Security" },
				{ key: "soar", label: "SOAR" },
				{ key: "casb", label: "CASB" },
				{ key: "sase", label: "SASE" },
				{ key: "otIcs", label: "OT / ICS" },
			],
		},
	],
	note:
		"Slack, Discord, and Microsoft Teams use familiar brand marks. Other tiles represent categories where integrations are API-enabled and implemented within each organization's environment.",
};

export const communityVelocity = {
	title: "Community that compounds",
	lead: "Join the Truss Discord to ask questions, learn faster, and share workflows with builders and analysts.",
	links: [
		{ label: "Join the Truss Discord", href: urls.discord },
		{ label: "Truss MCP", href: `#${sectionIds.developers}` },
		{ label: "Truss SDK for developers", href: urls.sdkReadme },
		{ label: "Truss Docs and API reference", href: urls.docsApiReadme },
	],
	notes: [
		"Every question, answer, and shared workflow strengthens the community around Truss.",
		"Use the SDK and docs to move from discovery to integration quickly.",
	],
};

export const proofPlaceholder = {
	title: "DEMO - Trusted by security teams",
	lead: "DEMO - Security teams use Truss to move intelligence into daily operations.",
	customers: [
		{ name: "Anthropic", wordmark: "ANTHROPIC", color: "#ffffff" },
		{ name: "Arctic Wolf", wordmark: "Arctic Wolf", color: "#ffffff" },
		{ name: "CrowdStrike", wordmark: "CrowdStrike", color: "#ff5f7a" },
		{ name: "CyberArk", wordmark: "CyberArk", color: "#ffffff" },
		{ name: "Darktrace", wordmark: "Darktrace", color: "#ffffff" },
		{ name: "Dropbox", wordmark: "Dropbox", color: "#ffffff" },
		{ name: "Elastic", wordmark: "Elastic", color: "#ffffff" },
		{ name: "Facebook", wordmark: "Facebook", color: "#ffffff" },
		{ name: "Google", wordmark: "Google", color: "#ffffff" },
		{ name: "Coinbase", wordmark: "coinbase", color: "#5f86ff" },
		{ name: "Microsoft", wordmark: "Microsoft", color: "#ffffff" },
		{ name: "Perplexity", wordmark: "perplexity", color: "#ffffff" },
		{ name: "HubSpot", wordmark: "HubSpot", color: "#ffffff" },
		{ name: "Notion", wordmark: "Notion", color: "#ffffff" },
		{ name: "PayPal", wordmark: "PayPal", color: "#00a3ff" },
		{ name: "Lovable", wordmark: "Lovable", color: "#ffffff" },
		{ name: "Datadog", wordmark: "Datadog", color: "#8f7bff" },
		{ name: "Cloudflare", wordmark: "Cloudflare", color: "#ffb24a" },
		{ name: "Okta", wordmark: "Okta", color: "#7ab3ff" },
		{ name: "TruKno", wordmark: "TruKno", color: "#ff5f7a" },
		{ name: "Tannium", wordmark: "Tannium", color: "#ffffff" },
		{ name: "Panther", wordmark: "Panther", color: "#ffffff" },
		{ name: "Red Canary", wordmark: "Red Canary", color: "#ffffff" },
		{ name: "Qualys", wordmark: "Qualys", color: "#ffffff" },
		{ name: "Rapid7", wordmark: "Rapid7", color: "#ffffff" },
		{ name: "SentinelOne", wordmark: "SentinelOne", color: "#ffffff" },
		{ name: "Sophos", wordmark: "Sophos", color: "#ffffff" },
		{ name: "Symantec", wordmark: "Symantec", color: "#ffffff" },
	],
};

export const footer = {
	tagline: "Truss Security Inc. · The Security Information Exchange",
	columns: [
		{
			title: "Product",
			links: [
				{ label: "What Truss does best", to: `/#${sectionIds.trust}` },
				{ label: "How Truss fits together", to: `/#${sectionIds.platform}` },
				{ label: "Integrations", to: `/#${sectionIds.integrations}` },
				{ label: "Pricing", to: "/pricing" },
				{ label: "Open dashboard", href: urls.dashboard },
			],
		},
		{
			title: "Developers",
			links: [
				{ label: "Truss API docs", href: urls.docsApiReadme },
				{ label: "Truss SDK repository", href: urls.sdkReadme },
				{ label: "Install SDK (npm)", href: urls.npmPackage },
				{ label: "truss-agent repository", href: urls.trussAgent },
				{ label: "Truss MCP on Smithery", href: urls.mcpSmithery },
				{ label: "Developers on homepage", to: `/#${sectionIds.developers}` },
			],
		},
		{
			title: "Community",
			links: [
				{ label: "GitHub", href: urls.githubOrg },
				{ label: "Join Discord", href: urls.discord },
				{ label: "Community section", to: `/#${sectionIds.community}` },
			],
		},
		{
			title: "Trust & security",
			links: [
				{ label: "What Truss does best", to: `/#${sectionIds.trust}` },
				{ label: "API-enabled destinations", to: `/#${sectionIds.integrations}` },
				{ label: "System status", href: publicUrls.status, optional: true },
				{ label: "Security disclosure — coming soon", to: "#" },
			],
		},
	],
	copyright: `© ${new Date().getFullYear()} Truss Security Inc. All rights reserved.`,
};

export const pricing = {
	title: "Pricing",
	lead:
		"Truss pricing is based on access to capabilities—not event counts or API volume. Choose the tier that matches how you explore, automate, and integrate threat intelligence.",
	footnote:
		"Access and Deployment are billed monthly. Enterprise includes all features scaled for larger security teams, MSSPs, MDR operations, and multi-team environments.",
	contactEmail: "info@truss-security.com",
	billingUrl: `${urls.dashboard}/billing`,
	plans: [
		{
			name: "Community",
			price: 0,
			priceDisplay: null,
			interval: "month",
			tagline: "Explore threat intelligence in the dashboard",
			description:
				"Full dashboard access for exploration, contributor discovery, and manual intelligence review. No API key, MCP, or Agent automation.",
			features: [
				"Full dashboard access",
				"Discord community",
				"API key + Truss Query Language (TQL)",
				"Truss Agent automation",
				"Truss MCP endpoint",
				"Truss Local MCP Agent",
			],
			included: [true, true, false, false, false, false],
			cta: { label: "Open dashboard", href: urls.dashboard, variant: "secondary" },
		},
		{
			name: "Access",
			price: 200,
			priceDisplay: null,
			interval: "month",
			tagline: "Automate with API access and the Truss Agent",
			description:
				"API key unlocks Truss Query Language (TQL) for programmatic queries and Truss Agent for scheduled delivery into your security stack.",
			features: [
				"Everything in Community",
				"API key + Truss Query Language (TQL)",
				"Truss Agent automation",
				"Commercial use",
				"Truss MCP endpoint",
				"Truss Local MCP Agent",
			],
			included: [true, true, true, true, false, false],
			cta: { label: "Upgrade to Access", href: `${urls.dashboard}/billing`, variant: "primary" },
		},
		{
			name: "Deployment",
			price: 500,
			priceDisplay: null,
			interval: "month",
			tagline: "Integrate Truss into your AI and LLM operations",
			description:
				"All Access features plus the Truss MCP endpoint for dynamic queries via your LLM and MCP stack, including the Truss Local MCP Agent.",
			features: [
				"Everything in Access",
				"Truss MCP endpoint (hosted + your MCP stack)",
				"Truss Local MCP Agent",
				"OAuth consent for MCP Apps",
				"Priority support",
				"Commercial use",
			],
			included: [true, true, true, true, true, true],
			cta: { label: "Upgrade to Deployment", href: `${urls.dashboard}/billing`, variant: "primary" },
		},
		{
			name: "Enterprise",
			price: null,
			priceDisplay: "Contact Truss",
			interval: null,
			tagline: "Enterprise-grade delivery at organizational scale",
			description:
				"All Access and Deployment capabilities at scale for MSSPs, MDR operations, and multi-team environments with dedicated support.",
			features: [
				"Everything in Deployment",
				"Multi-team / MSSP / MDR scale",
				"Per-client API keys",
				"Deep stack integration",
				"Dedicated support and onboarding",
				"Custom contract pricing",
			],
			included: [true, true, true, true, true, true],
			cta: {
				label: "Contact Truss",
				href: "mailto:info@truss-security.com?subject=Truss%20Enterprise%20Plan%20Inquiry",
				variant: "secondary",
			},
		},
	],
	comparison: {
		title: "Compare plans by feature",
		rows: [
			{ feature: "Dashboard access", community: true, access: true, deployment: true, enterprise: true },
			{ feature: "Discord community", community: true, access: true, deployment: true, enterprise: true },
			{
				feature: "API key + Truss Query Language (TQL)",
				community: false,
				access: true,
				deployment: true,
				enterprise: true,
			},
			{ feature: "Truss Agent automation", community: false, access: true, deployment: true, enterprise: true },
			{
				feature: "Truss MCP endpoint (hosted + your MCP stack)",
				community: false,
				access: false,
				deployment: true,
				enterprise: true,
			},
			{
				feature: "Truss Local MCP Agent (detection rules, IOC push)",
				community: false,
				access: false,
				deployment: true,
				enterprise: true,
			},
			{
				feature: "Multi-team / MSSP / MDR scale",
				community: false,
				access: false,
				deployment: false,
				enterprise: true,
			},
		],
	},
	explainers: [
		{
			title: "Access — API + Agent",
			body: "Paying users receive an API key to use the Truss Query Language (TQL) for programmatic queries and automation through the Truss Agent. Schedule jobs that query Truss with your saved filters and deliver formatted intelligence to Slack, Discord, Teams, and more.",
		},
		{
			title: "Deployment — API + MCP",
			body: "Includes all Access features plus the Truss MCP endpoint. Integrate Truss data into your AI/LLM operations through the Truss Local MCP Agent or your own MCP stack. Query products, build detection rules, and push IOCs to your security tools—powered by your favored LLM.",
		},
	],
};

export const siteMeta = {
	title: "Truss Security — The Security Information Exchange",
	description:
		"Customized threat intelligence — tailored, fast, and pre-validated. Delivered via dashboard, REST, STIX, MCP, and agents.",
	ogImage: `${siteOrigin}${brandAssets.icon92}`,
};

